
'use client';

import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import type { Category, NewCategory } from '@/types';
import { db } from '@/lib/firebase';
import { collection, onSnapshot, query, orderBy, writeBatch, getDocs, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from './AuthContext';

const initialCategories = [
    { name: '3D Modeling', slug: '3d-modeling' },
    { name: 'Hardsurface', slug: 'hardsurface' },
    { name: 'Props', slug: 'props' },
    { name: 'Environments', slug: 'environments' },
    { name: 'Architecture', slug: 'architecture' },
    { name: 'Animation and Rigging', slug: 'animation-and-rigging' },
    { name: 'Branding', slug: 'branding' },
    { name: 'Product Design', slug: 'product-design' },
];

interface CategoryContextType {
    categories: Category[];
    loading: boolean;
    createCategory: (category: NewCategory) => Promise<void>;
}

const CategoryContext = createContext<CategoryContextType | undefined>(undefined);

export const CategoryProvider = ({ children }: { children: ReactNode }) => {
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const { toast } = useToast();
    const { isOwner } = useAuth(); // isOwner is only for write operations now

    const initializeCategories = useCallback(async () => {
      if (!isOwner) return; // Only owner can initialize
        const categoriesCollection = collection(db, 'categories');
        const existingSnapshot = await getDocs(categoriesCollection);
        
        if (existingSnapshot.empty) {
            console.log("Initializing categories...");
            const batch = writeBatch(db);
            initialCategories.forEach(cat => {
                const catRef = doc(db, 'categories', cat.slug);
                batch.set(catRef, { ...cat, createdAt: serverTimestamp() });
            });
            await batch.commit();
            console.log("Categories initialized.");
        }
    }, [isOwner]);

    useEffect(() => {
        // Run initialization check once if owner
        initializeCategories();

        const q = query(collection(db, "categories"), orderBy("createdAt", "asc"));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const categoriesData: Category[] = [];
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                categoriesData.push({
                    id: doc.id,
                    name: data.name,
                    slug: data.slug,
                    createdAt: data.createdAt,
                });
            });
            setCategories(categoriesData);
            setLoading(false);
        }, (error) => {
            console.error("Firestore snapshot error (categories):", error);
            toast({
                title: "Connection Error",
                description: "Could not load categories.",
                variant: "destructive",
            });
            setLoading(false);
        });

        return () => unsubscribe();
    }, [toast, initializeCategories]);

    const createCategory = async (category: NewCategory) => {
        if (!isOwner) return;

        const slug = category.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        const categoryRef = doc(db, 'categories', slug);
        
        const newCategory = {
            name: category.name,
            slug: slug,
            createdAt: serverTimestamp(),
        }

        try {
            await setDoc(categoryRef, newCategory);
            toast({ title: "Category added", description: `The category "${category.name}" has been created.` });
        } catch (error) {
            console.error("Error creating category:", error);
            toast({ title: "Error", description: "Failed to create the category.", variant: "destructive" });
        }
    };
    
    return (
        <CategoryContext.Provider value={{ categories, loading, createCategory }}>
            {children}
        </CategoryContext.Provider>
    );
};

export const useCategory = () => {
    const context = useContext(CategoryContext);
    if (context === undefined) {
        throw new Error('useCategory must be used within a CategoryProvider');
    }
    return context;
};
